import type { ReactNode } from 'react'

import { useCallback, useEffect, useRef } from 'react'

import type { ToastDuration, ToastOptions } from './toast.types'

import { useVuiContext } from '../core/vuiProvider/context'
import { uid } from '../utils'
import { ToastItem } from './Toaster'

export const defaultDuration = 'fast'
export const fastToastDuration = 4000
export const mediumToastDuration = 6000
export const slowToastDuration = 8000

const getDurationTime = (duration: Exclude<ToastDuration, 'sticky'>): number => {
  switch (duration) {
    case 'fast':
      return fastToastDuration
    case 'medium':
      return mediumToastDuration
    case 'slow':
      return slowToastDuration
    default: {
      const _exhaustive: never = duration
      throw new Error(`[VUI] useToast: unhandled duration "${_exhaustive}"`)
    }
  }
}

export const useToast = () => {
  const { addToast, removeToast } = useVuiContext()
  const timeoutMapRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    const timeouts = timeoutMapRef.current
    return () => {
      timeouts.forEach(clearTimeout)
      timeouts.clear()
    }
  }, [])

  const showToast = useCallback(
    (options?: ToastOptions): void => {
      if (process.env.NODE_ENV !== 'production' && options?.dismissible !== undefined) {
        console.warn(
          '[VUI] Toast: `dismissible` is deprecated. Use `showDismissButton` to control dismiss button visibility.',
        )
      }
      const id = options?.id ? options.id : uid('toast')
      const showDismissButton = options?.showDismissButton ?? options?.dismissible ?? true
      const { duration = defaultDuration, status = 'info', action, icon, size, text, textLayout, title } = options || {}

      const handleClose = () => {
        const timeoutId = timeoutMapRef.current.get(id)
        if (timeoutId !== undefined) clearTimeout(timeoutId)
        timeoutMapRef.current.delete(id)
        removeToast(id)
      }

      const toast = {
        id,
        component: (
          <ToastItem
            action={action}
            icon={icon}
            showDismissButton={showDismissButton}
            size={size}
            status={status}
            text={text}
            textLayout={textLayout}
            title={title}
            onClose={handleClose}
          />
        ),
      }

      addToast(toast)

      if (duration && duration !== 'sticky') {
        timeoutMapRef.current.set(
          id,
          setTimeout(() => {
            timeoutMapRef.current.delete(id)
            removeToast(id)
          }, getDurationTime(duration)),
        )
      }
    },
    [addToast, removeToast],
  )

  const showInfo = useCallback(
    (
      text: ReactNode,
      duration: ToastDuration = defaultDuration,
      options?: Omit<ToastOptions, 'text' | 'status' | 'duration'>,
    ) => showToast({ text, status: 'info', duration, ...options }),
    [showToast],
  )

  const showSuccess = useCallback(
    (
      text: ReactNode,
      duration: ToastDuration = defaultDuration,
      options?: Omit<ToastOptions, 'text' | 'status' | 'duration'>,
    ) => showToast({ text, status: 'success', duration, ...options }),
    [showToast],
  )

  const showWarning = useCallback(
    (
      text: ReactNode,
      duration: ToastDuration = 'medium',
      options?: Omit<ToastOptions, 'text' | 'status' | 'duration'>,
    ) => showToast({ text, status: 'warning', duration, ...options }),
    [showToast],
  )

  const showError = useCallback(
    (text: ReactNode, duration: ToastDuration = 'slow', options?: Omit<ToastOptions, 'text' | 'status' | 'duration'>) =>
      showToast({ text, status: 'error', duration, ...options }),
    [showToast],
  )

  const showLoading = useCallback(
    (
      text: ReactNode,
      duration: ToastDuration = 'sticky',
      options?: Omit<ToastOptions, 'text' | 'status' | 'duration'>,
    ) => showToast({ text, status: 'loading', duration, ...options }),
    [showToast],
  )

  return {
    showToast,
    hideToast: removeToast,
    showInfo,
    showSuccess,
    showWarning,
    showError,
    showLoading,
  }
}
